'use client';

import { Flex, Icon, Text } from '@chakra-ui/react';
import Link from 'next/link';
import { useContext, useEffect, useMemo, useState } from 'react';
import { FaStore, FaTruck } from 'react-icons/fa6';
import { api } from '@/services/api';
import { CartContext } from '@/contexts/CartContext';
import { usePromotions } from '@/contexts/PromotionsContext';

const fmt = (value: number) =>
  new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(
    value,
  );

const muted = { base: 'rgba(0,0,0,0.55)', _dark: 'rgba(255,255,255,0.5)' };
const strong = { base: '#0A1B2B', _dark: '#E2EFF5' };
const rowBg = { base: 'rgba(0,0,0,0.03)', _dark: 'rgba(255,255,255,0.05)' };

/**
 * Opções de entrega por baixo do "Adicionar ao carrinho".
 *
 * O limite dos portes grátis vem da regra de carrinho ativa (a mesma que o
 * checkout aplica — ver checkCartFreeShipping no backend), não fica fixo
 * aqui: se mudar no backoffice, a página acompanha. A comparação usa o
 * preço antes de descontos, tal como o backend faz no carrinho.
 *
 * `productTotal` é o preço (antes de descontos) da opção escolhida × a
 * quantidade — só serve para avisar, no texto, quando adicionar este
 * artigo leva o carrinho acima do limite.
 */
const DeliveryOptions = ({
  variant,
  productTotal,
}: {
  variant: 'full' | 'compact';
  productTotal: number;
}) => {
  const { items } = useContext(CartContext);
  const { promotions } = usePromotions();
  const [cheapestShipping, setCheapestShipping] = useState<number | null>(
    null,
  );

  useEffect(() => {
    let active = true;
    api
      .get<{ price: number }[]>('/shipments/list/simple?country_code=PT')
      .then((response) => {
        if (!active || response.data.length === 0) return;
        setCheapestShipping(Math.min(...response.data.map((s) => s.price)));
      })
      .catch(() => {
        // Sem preço o bloco continua útil (só não mostra o "desde X €").
      });
    return () => {
      active = false;
    };
  }, []);

  // null = não há nenhuma campanha de portes grátis ativa.
  const threshold = useMemo(() => {
    const rules = promotions.filter((rule) => rule.free_shipping);
    if (rules.length === 0) return null;
    if (rules.some((rule) => !rule.minimum_value)) return 0;
    return Math.min(...rules.map((rule) => rule.minimum_value as number));
  }, [promotions]);

  const cartTotal = useMemo(
    () =>
      items.reduce(
        (sum, item) =>
          sum + (item.original_total_price_tax ?? item.total_price_tax),
        0,
      ),
    [items],
  );

  let homePrice: string | null =
    cheapestShipping !== null ? `desde ${fmt(cheapestShipping)}` : null;
  let homeHint: string | null = null;
  let homeFree = false;

  if (threshold !== null) {
    // O "Grátis" à direita é sempre o estado atual do carrinho — nunca se
    // antecipa por causa deste artigo (um carrinho de 39,80 € a dizer
    // "Grátis" parecia que já estava). Se este artigo chega para passar o
    // limite, isso fica dito no texto de baixo.
    if (cartTotal >= threshold) {
      homeFree = true;
      if (threshold > 0) homeHint = `Em compras acima de ${fmt(threshold)}`;
    } else {
      const missing = fmt(threshold - cartTotal);
      homeHint =
        cartTotal + productTotal >= threshold
          ? `Faltam ${missing}, com este artigo fica grátis`
          : `Faltam ${missing} para envio grátis`;
    }
  }
  if (homeFree) homePrice = 'Grátis';

  const storesLink = (
    <Link
      href="/lojas"
      style={{ textDecoration: 'underline', color: 'inherit' }}
    >
      Ver lojas
    </Link>
  );

  if (variant === 'compact') {
    return (
      <Flex
        flexDir="column"
        gap="8px"
        p="12px 14px"
        borderRadius="12px"
        bg={rowBg}
        fontSize="13px"
      >
        <Flex align="center" gap="10px">
          <Icon w="14px" h="14px" color={strong} flexShrink={0}>
            <FaStore />
          </Icon>
          <Text flex="1" color={strong}>
            Levantamento em loja{' '}
            <Text as="span" color={muted} fontSize="12px">
              · {storesLink}
            </Text>
          </Text>
          <Text fontWeight="700" color="#00B0CA">
            Grátis
          </Text>
        </Flex>
        <Flex align="flex-start" gap="10px">
          <Icon w="14px" h="14px" color={strong} flexShrink={0} mt="2px">
            <FaTruck />
          </Icon>
          <Flex flex="1" flexDir="column">
            <Text color={strong}>Entrega em casa</Text>
            {homeHint && (
              <Text color={muted} fontSize="12px">
                {homeHint}
              </Text>
            )}
          </Flex>
          {homePrice && (
            <Text
              fontWeight="700"
              color={homeFree ? '#00B0CA' : strong}
              whiteSpace="nowrap"
            >
              {homePrice}
            </Text>
          )}
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex flexDir="column" gap="14px" mt="18px">
      <Flex flexDir="column" gap="8px">
        <Flex align="center" gap="8px">
          <Icon w="15px" h="15px" color={strong}>
            <FaStore />
          </Icon>
          <Text fontSize="14px" fontWeight="700" color={strong}>
            Levanta na loja
          </Text>
        </Flex>
        <Flex
          align="center"
          justify="space-between"
          p="12px 16px"
          borderRadius="10px"
          bg={rowBg}
        >
          <Flex flexDir="column">
            <Text fontSize="14px" color={strong}>
              Levantamento em loja
            </Text>
            <Text fontSize="13px" color={muted}>
              {storesLink}
            </Text>
          </Flex>
          <Text fontSize="14px" fontWeight="700" color="#00B0CA">
            Grátis
          </Text>
        </Flex>
      </Flex>

      <Flex flexDir="column" gap="8px">
        <Flex align="center" gap="8px">
          <Icon w="15px" h="15px" color={strong}>
            <FaTruck />
          </Icon>
          <Text fontSize="14px" fontWeight="700" color={strong}>
            Recebe em casa
          </Text>
        </Flex>
        <Flex
          align="center"
          justify="space-between"
          gap="12px"
          p="12px 16px"
          borderRadius="10px"
          bg={rowBg}
        >
          <Flex flexDir="column">
            <Text fontSize="14px" color={strong}>
              Entrega em casa
            </Text>
            {homeHint && (
              <Text fontSize="13px" color={muted}>
                {homeHint}
              </Text>
            )}
          </Flex>
          {homePrice && (
            <Text
              fontSize="14px"
              fontWeight="700"
              color={homeFree ? '#00B0CA' : strong}
              whiteSpace="nowrap"
            >
              {homePrice}
            </Text>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};

export default DeliveryOptions;
